/**
 * Turning the note editor's codes into words.
 *
 * Nothing here holds state and nothing renders: the editor reads these, so that the
 * mapping from a refusal code to a message is testable without a DOM and the wording is
 * reviewable in one place.
 *
 * **Every code gets a message.** `noteRefusalMessage` is exhaustive over
 * {@link NoteRefusalCode} by construction, so a code added on the server and typed into
 * `NOTE_REFUSAL_CODES` fails the typecheck here until it has been given wording. The
 * alternative — a default branch — is a blank or generic message on the one screen where
 * the clinician is holding text that has not been written anywhere.
 */

import type { TFunction } from 'i18next'

import { formatClinicalDateTime } from '../format/clinicalDate'
import { guarded } from '../patient/patientDisplay'
import { practitionerName } from '../patient/patientOverviewDisplay'
import type { NoteRefusalCode, NoteRefusalParams, NoteVersion } from './noteApi'
import type { DraftProblem, NoteField } from './noteDraft'

/**
 * The lookup the editor needs.
 *
 * `common` first, matching `ward/wardDisplay.ts` — see its remarks. Every key below
 * names its namespace regardless.
 *
 * **`patient` is in the list on purpose**, and it is not a layering slip: the four SOAP
 * headings and the three note-type names already exist there because the reading view
 * renders them, and the box a clinician types into must be labelled with the same word
 * the note is read under. Two catalogue entries holding `Bedömning` with nothing keeping
 * them equal is a heading that drifts the first time a clinical reviewer rewords one of
 * them. `notes` holds what only the editor has: its chrome, the hints, the refusals, and
 * `field.bodyText` — the one label with no counterpart, because the reading view shows
 * free text as a paragraph with no heading at all.
 */
export type NoteTranslate = TFunction<['common', 'patient', 'notes']>

/** A field's heading, reused as the name in a "too long" message. */
export function noteFieldLabel(field: NoteField, t: NoteTranslate): string {
  switch (field) {
    case 'subjective':
      return t('patient:notes.subjective')
    case 'objective':
      return t('patient:notes.objective')
    case 'assessment':
      return t('patient:notes.assessment')
    case 'plan':
      return t('patient:notes.plan')
    case 'bodyText':
      return t('notes:field.bodyText')
  }
}

/**
 * The sentence under a field, saying what belongs in it.
 *
 * Editor-only, so these are `notes` keys throughout — the reading view has no use for
 * them. They are the thin end of what `PHASE-0.md` §2.5 wants from the interviews: the
 * SOAP shape is unfamiliar to some of the staff who would use this, and a heading alone
 * does not say whether a measurement goes under objective or assessment.
 */
export function noteFieldHint(field: NoteField, t: NoteTranslate): string {
  switch (field) {
    case 'subjective':
      return t('notes:fieldHint.subjective')
    case 'objective':
      return t('notes:fieldHint.objective')
    case 'assessment':
      return t('notes:fieldHint.assessment')
    case 'plan':
      return t('notes:fieldHint.plan')
    case 'bodyText':
      return t('notes:fieldHint.bodyText')
  }
}

/**
 * A field name as it arrived in a `NOTE_FIELD_TOO_LONG` param, in words.
 *
 * Falls back to the raw name rather than dropping it: the message's job is to say which
 * box to shorten, and a server field this interface has not met is still better named
 * than not named.
 */
function fieldNameFromParams(field: string | undefined, t: NoteTranslate): string {
  switch (field) {
    case 'subjective':
    case 'objective':
    case 'assessment':
    case 'plan':
    case 'bodyText':
      return noteFieldLabel(field, t)
    default:
      return field ?? ''
  }
}

/**
 * What to tell the clinician about a refusal.
 *
 * The `UNKNOWN` case is not a fallback for laziness — it is what a proxy error page or a
 * future code reaches, and its message says the note was not written, which is the one
 * fact that matters and is true of every refusal.
 */
export function noteRefusalMessage(
  code: NoteRefusalCode | 'UNKNOWN',
  params: NoteRefusalParams,
  t: NoteTranslate,
): string {
  switch (code) {
    case 'NOTE_EMPTY':
      return t('notes:refusal.empty')

    case 'NOTE_TYPE_MISSING':
      return t('notes:refusal.typeMissing')

    case 'NOTE_TYPE_INVALID':
      return t('notes:refusal.typeInvalid')

    case 'NOTE_FIELD_TOO_LONG':
      // The server's `maximum`, not the client constant, so a drift between the two
      // shows up as a disagreement a reader can see rather than a wrong number.
      return t('notes:refusal.fieldTooLong', {
        field: fieldNameFromParams(params.field, t),
        maximum: params.maximum ?? 0,
      })

    case 'NOTE_LANGUAGE_INVALID':
      return t('notes:refusal.languageInvalid')

    case 'NOTE_VERSION_STALE':
      // The recoverable one, and the only refusal with something for the clinician to
      // decide. The editor pairs this with a reload action; the message's job is to say
      // that their text is still on screen and somebody else's is now in the record.
      return t('notes:refusal.versionStale')

    case 'NOTE_RETRACTED':
      return t('notes:refusal.retracted')

    case 'NOTE_SUPERSEDES_MISSING':
    case 'NOTE_SUPERSEDES_UNEXPECTED':
      // Both are this client disagreeing with itself about whether it is correcting
      // something — not anything a clinician can act on. One message, saying the note
      // was not written and to try again, which is the honest description.
      return t('notes:refusal.clientMismatch')

    case 'NOTE_NOT_FOUND':
      return t('notes:refusal.noteNotFound')

    case 'PATIENT_NOT_FOUND':
      return t('notes:refusal.patientNotFound')

    case 'NOTE_BODY_MISSING':
    case 'UNKNOWN':
      return t('notes:refusal.unknown')
  }
}

/**
 * What to tell the clinician when the write could not be attempted, or answered
 * something unusable.
 *
 * Separate wording from every refusal, because the action is opposite: a refusal is
 * something to fix in the form, and this is something to wait out — so the message says
 * to leave the window open. It is the one message on this screen whose job is to stop the
 * clinician from closing the only copy of their text.
 */
export function noteWriteUnavailableMessage(t: NoteTranslate): string {
  return t('notes:refusal.unavailable')
}

/** The same, for a problem this side found before sending. */
export function draftProblemMessage(problem: DraftProblem, t: NoteTranslate): string {
  switch (problem.code) {
    case 'NOTE_TYPE_MISSING':
      return t('notes:refusal.typeMissing')
    case 'NOTE_LANGUAGE_MISSING':
      return t('notes:refusal.languageMissing')
    case 'NOTE_EMPTY':
      return t('notes:refusal.empty')
    case 'NOTE_FIELD_TOO_LONG':
      return t('notes:refusal.fieldTooLong', {
        field: noteFieldLabel(problem.field, t),
        maximum: problem.maximum,
      })
  }
}

/**
 * A note type in words, for the editor's select.
 *
 * The reading view's keys, for the reason {@link NoteTranslate} gives: the option a
 * clinician picks and the heading their note then carries must be the same word. This is
 * `patientOverviewDisplay.noteTypeLabel` with a different `t` type and nothing else — not
 * worth sharing across the two modules, worth keeping on the same catalogue keys.
 */
export function offeredNoteTypeLabel(noteType: string, t: NoteTranslate): string {
  switch (noteType) {
    case 'daily':
      return t('patient:notes.typeDaily')
    case 'admission':
      return t('patient:notes.typeAdmission')
    case 'discharge':
      return t('patient:notes.typeDischarge')
    default:
      // The vocabulary is open in the database (migration 0007). Showing the code beats
      // hiding the option — the same fallback `noteTypeLabel` makes when reading.
      return noteType
  }
}

/**
 * How this version's status reads in the history.
 *
 * `signed` and `amended` are not distinguished here, and that is deliberate: which one a
 * row carries is a fact about how it was written, and what a reader needs is whether it is
 * the version in force. {@link noteVersionStanding} answers that. What this reports is the
 * two states that are neither — a retraction and an unsigned row — because both mean the
 * text above must not be read as the record's current answer.
 */
export function noteVersionException(
  version: NoteVersion,
  t: NoteTranslate,
): string | null {
  if (version.status === 'entered-in-error') {
    return t('notes:version.retracted')
  }
  if (version.signedAt === null) {
    // A version with no signature is a real state, not an error (see NoteVersion). Phase 0
    // writes none — the write path signs in the same transaction — so seeing this means
    // either a later phase's draft path or a signature that did not survive, and a reader
    // has to be told which text they are looking at.
    return t('notes:version.unsigned')
  }
  return null
}

/** Whether this is the version in force, given the newest version number in the note. */
export function noteVersionStanding(
  version: NoteVersion,
  newest: number,
  t: NoteTranslate,
): string {
  return version.version === newest
    ? t('notes:history.current')
    : t('notes:history.superseded')
}

/**
 * Who wrote this version, and when its content is clinically true.
 *
 * `effectiveAt` rather than `recordedAt`, matching the reading view: it is the same on
 * every version of one note, which is what keeps a correction where a reader expects to
 * find it (AGENTS.md §4). When this version was *written* is
 * {@link noteVersionWritten}, and in a history that is the interesting one — so both are
 * shown, always, rather than only when they differ.
 */
export function noteVersionAuthor(version: NoteVersion, t: NoteTranslate): string {
  const name = practitionerName(version.authorGivenName, version.authorFamilyName, t)
  const time = guarded(() => formatClinicalDateTime(version.effectiveAt), t)

  return version.authorRoleTitle === null
    ? t('patient:notes.attribution', { name, time })
    : t('patient:notes.attributionWithTitle', {
        title: version.authorRoleTitle,
        name,
        time,
      })
}

/** When this version was written into the record. */
export function noteVersionWritten(version: NoteVersion, t: NoteTranslate): string {
  return t('notes:version.written', {
    time: guarded(() => formatClinicalDateTime(version.recordedAt), t),
  })
}

/**
 * Who signed this version and when, or that it is not signed.
 *
 * The signer is named separately from the author because they are separate columns and
 * will be separate people as soon as authentication lands — a note written by one
 * practitioner and counter-signed by another is a real thing (`NoteVersionsQuery`).
 */
export function noteVersionSignature(version: NoteVersion, t: NoteTranslate): string {
  const signedAt = version.signedAt
  if (signedAt === null) {
    return t('notes:version.unsigned')
  }

  const name = practitionerName(version.signerGivenName, version.signerFamilyName, t)
  const time = guarded(() => formatClinicalDateTime(signedAt), t)

  return version.signerRoleTitle === null
    ? t('notes:version.signedBy', { name, time })
    : t('notes:version.signedByWithTitle', {
        title: version.signerRoleTitle,
        name,
        time,
      })
}

/**
 * The clock behind the signing time, in words.
 *
 * **Shown here and nowhere else.** DECISIONS.md #37: a signing time is worth exactly what
 * the clock behind it is worth, and on a box that has been isolated for days that is not a
 * formality — a box that booted with no time source signs with its realtidsklocka and says
 * so. Putting it in the version history rather than on every note keeps it where somebody
 * is already asking when something was written, instead of adding a caveat to text a
 * clinician is reading for clinical reasons.
 *
 * Null when the version is not signed, since there is no time for a clock to qualify.
 */
export function noteVersionClock(version: NoteVersion, t: NoteTranslate): string | null {
  if (version.signedAt === null || version.clockSource === null) {
    return null
  }

  const source = clockSourceLabel(version.clockSource, t)

  // **Zero is a claim, not a missing value** — `±0 s` says the box asserted exact
  // agreement, which is different from asserting nothing. So the test is against null.
  return version.clockUncertaintySeconds === null
    ? t('notes:version.clock', { source })
    : t('notes:version.clockWithUncertainty', {
        source,
        seconds: version.clockUncertaintySeconds,
      })
}

/**
 * A clock source in words.
 *
 * The four values migration 0012's CHECK allows (`DECISIONS.md` #37). The fallback shows
 * the code: #37 records a fifth value, `unverified`, as parked, and a box that starts
 * writing it must not produce a blank line in a signature.
 */
export function clockSourceLabel(source: string, t: NoteTranslate): string {
  switch (source) {
    case 'gps':
      return t('notes:clock.gps')
    case 'ntp':
      return t('notes:clock.ntp')
    case 'rtc':
      return t('notes:clock.rtc')
    case 'manual':
      return t('notes:clock.manual')
    default:
      return source
  }
}
